import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ChartBar, Users, CheckSquare, Warning, Leaf, TrendUp, Clock,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { STATUS_COLORS, fmtDateTime } from "../lib/utils";
import { Link } from "react-router-dom";
import DateRangeFilter from "../components/crm/DateRangeFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const fmtEuro = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

function Kpi({ label, value, Icon, tint = "#1A3A21", testid, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</div>
          <div className="mt-1 font-display text-3xl font-bold text-gray-900">{value}</div>
          {sub && <div className="text-[11px] text-gray-500 mt-1 leading-tight min-h-[1.75rem]">{sub}</div>}
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${tint}14`, color: tint }}
        >
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [revRange, setRevRange] = useState({ preset: "up_to_today", start_date: "", end_date: "" });
  const [revAgent, setRevAgent] = useState("all");

  useEffect(() => {
    api.get("/dashboard/admin").then(({ data }) => setData(data));
    api.get("/users").then(({ data }) => setAgents(data));
  }, []);

  useEffect(() => {
    const params = { date_preset: revRange.preset };
    if (revRange.preset === "custom") {
      if (revRange.start_date) params.start_date = revRange.start_date;
      if (revRange.end_date) params.end_date = revRange.end_date;
    }
    if (revAgent !== "all") params.agent_id = revAgent;
    api.get("/dashboard/admin/revenue", { params }).then(({ data }) => setRevenue(data)).catch(() => setRevenue(null));
  }, [revRange, revAgent]);

  if (!data) return <div className="text-gray-500">Loading...</div>;

  const statusData = Object.entries(data.by_status).map(([name, value]) => ({ name, value }));
  const agentsMap = Object.fromEntries(agents.map((a) => [a.id, a]));
  const activityByAgentData = Object.entries(data.activities_by_agent || {}).map(([id, count]) => ({
    name: id === "_unassigned" ? "Unassigned" : (agentsMap[id]?.name || id?.slice(0, 6) || "Unknown"),
    count,
  }));
  // UI-STABILITY-1B-tier1: precompute the max for the country bar chart once
  // per render — previous code re-mapped+reduced inside every row iteration.
  const maxCountryCount = data.leads_by_country.length
    ? Math.max(...data.leads_by_country.map((x) => x.count))
    : 0;
  // UI-STABILITY-1B-tier1: filter agents once per render to feed the Select.
  const adminAgentOptions = agents.filter((a) => a.role === "agent");

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">
            {user?.role === "admin" ? "Admin overview" : "Overview"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Here&apos;s what&apos;s happening across your pipeline today.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Leads" value={data.total_leads} Icon={Leaf} tint="#1A3A21" testid="kpi-total-leads"
          sub="All active leads" />
        <Kpi label="New" value={data.by_status.New || 0} Icon={TrendUp} tint="#0EA5E9" testid="kpi-new"
          sub="No activity yet" />
        <Kpi label="Qualified" value={data.by_status.Qualified || 0} Icon={ChartBar} tint="#10B981" testid="kpi-qualified"
          sub="Commercial activity" />
        <Kpi label="Customers" value={data.by_status.Customer || 0} Icon={CheckSquare} tint="#1A3A21" testid="kpi-customers"
          sub="Recent ORDER" />
        <Kpi label="Sleepy" value={data.by_status.Sleepy || 0} Icon={Clock} tint="#F59E0B" testid="kpi-sleepy"
          sub="Idle 30+ days" />
        <Kpi label="Lost" value={data.by_status.Lost || 0} Icon={Warning} tint="#EF4444" testid="kpi-lost"
          sub="Idle 90+ days" />
        <Kpi label="Open Activities" value={data.open_activities ?? 0} Icon={CheckSquare} tint="#E1A104"
          testid="kpi-open-activities" sub="Still to complete" />
        <Kpi label="Overdue Activities" value={data.overdue_activities ?? 0} Icon={Warning} tint="#EF4444"
          testid="kpi-overdue-activities" sub="Past due date" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5 lg:col-span-2" data-testid="chart-leads-by-status">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Pipeline by Status</h3>
            <span className="text-xs text-gray-500">{data.total_leads} leads</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusData} margin={{ top: 10, right: 10, left: -10, bottom: 30 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} interval={0} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((d) => (
                  <Cell key={d.name} fill={STATUS_COLORS[d.name] || "#1A3A21"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="chart-activity-by-agent">
          <h3 className="font-display text-lg font-bold mb-4">Activities by Agent</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={activityByAgentData} dataKey="count" nameKey="name"
                innerRadius={50} outerRadius={90} paddingAngle={3}>
                {activityByAgentData.map((entry, i) => (
                  <Cell key={`agent-cell-${entry.name || i}`} fill={["#1A3A21", "#E1A104", "#10B981", "#0EA5E9", "#F59E0B"][i % 5]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={24} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="leads-by-country">
          <h3 className="font-display text-lg font-bold mb-4">Leads by Country</h3>
          <div className="space-y-2">
            {data.leads_by_country.slice(0, 8).map((r) => {
              return (
                <div key={r.country} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-gray-700 truncate">{r.country}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded">
                    <div className="h-2 rounded bg-[#1A3A21]"
                      style={{ width: `${maxCountryCount ? (r.count / maxCountryCount) * 100 : 0}%` }} />
                  </div>
                  <div className="w-8 text-right text-sm font-semibold">{r.count}</div>
                </div>
              );
            })}
            {!data.leads_by_country.length && <div className="text-sm text-gray-500">No data</div>}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="recent-activities">
          <h3 className="font-display text-lg font-bold mb-4">Recent Activities</h3>
          <div className="space-y-2">
            {data.recent_activities.slice(0, 6).map((a) => (
              <Link to={`/leads/${a.lead_id}`} key={a.id}
                className="flex items-start gap-3 p-2 rounded-md hover:bg-gray-50 transition-colors">
                <div className="mt-0.5 text-[11px] font-bold uppercase text-[#E1A104] w-14">
                  {a.activity_type.replace("_", " ")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{a.title}</div>
                  <div className="text-[11px] text-gray-500">{fmtDateTime(a.created_at)}</div>
                </div>
              </Link>
            ))}
            {!data.recent_activities.length && <div className="text-sm text-gray-500">No recent activities</div>}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="revenue-overview">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Revenue</div>
            <h3 className="font-display text-xl font-bold tracking-tight">Revenue Overview</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-44">
              <Select value={revAgent} onValueChange={setRevAgent}>
                <SelectTrigger data-testid="revenue-agent-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {adminAgentOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DateRangeFilter
              preset={revRange.preset}
              startDate={revRange.start_date}
              endDate={revRange.end_date}
              onChange={setRevRange}
              testidPrefix="admin-revenue-range"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="border border-gray-200 rounded-md p-4" data-testid="revenue-total">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Revenue</div>
            <div className="font-display text-3xl font-bold mt-1">{revenue ? fmtEuro(revenue.total_revenue) : "—"}</div>
          </div>
          <div className="border border-gray-200 rounded-md p-4" data-testid="revenue-order-count">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">ORDER Count</div>
            <div className="font-display text-3xl font-bold mt-1">{revenue?.order_count ?? "—"}</div>
          </div>
          <div className="border border-gray-200 rounded-md p-4" data-testid="revenue-aov">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Average Order Value</div>
            <div className="font-display text-3xl font-bold mt-1">{revenue ? fmtEuro(revenue.average_order_value) : "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-testid="revenue-by-agent">
            <h4 className="font-display text-sm font-bold mb-3">Revenue by Agent</h4>
            {revenue?.revenue_by_agent?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenue.revenue_by_agent} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                  <XAxis dataKey="agent_name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtEuro(v)} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#1A3A21" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-gray-500 py-6">No revenue in this range.</div>}
          </div>
          <div data-testid="revenue-top-leads">
            <h4 className="font-display text-sm font-bold mb-3">Top 10 Leads by Revenue</h4>
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Company</th>
                    <th className="text-left py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Agent</th>
                    <th className="text-right py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Revenue</th>
                    <th className="text-right py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Orders</th>
                    <th className="text-left py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Last ORDER</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue?.top_leads_by_revenue?.length ? revenue.top_leads_by_revenue.map((r) => (
                    <tr key={r.lead_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">
                        <Link to={`/leads/${r.lead_id}`} className="text-[#1A3A21] font-medium hover:underline">
                          {r.company_name}
                        </Link>
                      </td>
                      <td className="py-2 px-3 text-gray-600">{r.assigned_agent_name || "—"}</td>
                      <td className="py-2 px-3 text-right font-semibold">{fmtEuro(r.revenue)}</td>
                      <td className="py-2 px-3 text-right">{r.order_count}</td>
                      <td className="py-2 px-3 text-gray-600">{r.last_order_date || "—"}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="py-4 px-3 text-center text-gray-500">No data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
